'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { Flex } from '@skin-seoul/design-system';
import { useReviewService } from '@skin-seoul/service';
import type { Review } from '@skin-seoul/service';
import CustomerTestimonialsSwiperSkeleton from './CustomerTestimonialsSwiperSkeleton';
import { Carousel, type CarouselApi } from '@/components/ui/Carousel';
import { CarouselNavigation } from '@/components/ui/Carousel';
import TestimonialCard from './TestimonialCard';
import { extractProductIdsFromReviews, createProductMap } from '../utils/productUtils';
import { useProductService } from '@skin-seoul/service';
import { useRegionCurrency } from '@skin-seoul/service';
const CustomerTestimonialsSwiper = () => {
  const {
    currency
  } = useRegionCurrency();
  const [api, setApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);
  const {
    useGetCuratedReviews
  } = useReviewService();
  const {
    useGetSuspenseProductsByIds: useGetProductsByIds
  } = useProductService();
  const {
    data: reviewData
  } = useGetCuratedReviews();
  const titledReviews = useMemo(() => reviewData?.filter(review => review.title), [reviewData]);
  const productIds = useMemo(() => extractProductIdsFromReviews(titledReviews), [titledReviews]);
  const {
    data: productData
  } = useGetProductsByIds({
    ids: productIds,
    currency
  });
  const productMap = useMemo(() => {
    if (!productData) {
      return {};
    }
    return createProductMap(productData);
  }, [productData]);
  useEffect(() => {
    if (!api) {
      return;
    }
    const handleSelect = () => {
      setActiveIndex(api.selectedScrollSnap());
    };
    api.on('select', handleSelect);
    api.on('reInit', handleSelect);
    handleSelect();
    return () => {
      api.off('select', handleSelect);
    };
  }, [api]);
  return <Flex direction="column" width="100%" position="relative" gap={12} data-sentry-element="Flex" data-sentry-component="CustomerTestimonialsSwiper" data-sentry-source-file="CustomerTestimonialsSwiper.tsx">
      <Flex position="relative" width="100%" overflow="hidden" data-sentry-element="Flex" data-sentry-source-file="CustomerTestimonialsSwiper.tsx">
        <Carousel setApi={setApi} opts={{
        loop: true,
        align: 'start',
        skipSnaps: false
      }} w="100%" data-sentry-element="Carousel" data-sentry-source-file="CustomerTestimonialsSwiper.tsx">
          <Carousel.Content data-sentry-element="Carousel.Content" data-sentry-source-file="CustomerTestimonialsSwiper.tsx">
            {titledReviews.slice(0, 10).map((review: Review, index: number) => {
            const product = productMap[review.productId];
            return <Carousel.Item key={`${review.reviewId}-${index}`} flexBasis={{
              base: '100%',
              tablet: '50%',
              desktop: '33.333%'
            }} paddingX={3}>
                  <TestimonialCard review={review} product={product} />
                </Carousel.Item>;
          })}
          </Carousel.Content>
        </Carousel>
      </Flex>

      <CarouselNavigation api={api} activeIndex={activeIndex} totalItems={Math.min(titledReviews.length, 10)} data-sentry-element="CarouselNavigation" data-sentry-source-file="CustomerTestimonialsSwiper.tsx" />
    </Flex>;
};
const CustomerTestimonialsSwiperWithSkeleton = CustomerTestimonialsSwiper as typeof CustomerTestimonialsSwiper & {
  Skeleton: typeof CustomerTestimonialsSwiperSkeleton;
};
CustomerTestimonialsSwiperWithSkeleton.Skeleton = CustomerTestimonialsSwiperSkeleton;
export default CustomerTestimonialsSwiperWithSkeleton;